/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Completion Page - Shows order details and previous designs
 */

import React, { useState, useEffect } from 'react';
import { Copy, Check, ShoppingCart, Sparkles, Clock, ChevronLeft } from 'lucide-react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import logo from '../data/banner/logo-xoa-background.png';
import { indexedDBService } from '../services/indexedDbService';
import { addToHaravanCart } from '../services/haravanCartService';

interface DesignInfo {
  orderId: string;
  productType: string;
  productName?: string;
  mockupImageUrl: string;
  designOptions?: {
    // Phone case options
    removeBackground?: boolean;
    engravingStyle?: string;
    textOverlay?: string;
    printStyle?: string;
    // Cup customizer options
    printType?: string;
    scale?: number;
    position?: { x: number; y: number };
  };
}

interface PreviousDesign {
  id: string;
  productType: string;
  thumbnailUrl?: string; // Optional to avoid localStorage quota issues
  createdAt: Date;
  orderId: string;
  variantId?: number; // Haravan variant ID for re-purchasing
  productName?: string; // Product name for display
}

interface CompletionPageProps {
  designInfo: DesignInfo;
  onBackToShop?: () => void;
  shopUrl?: string; // Deprecated: use variantId instead
  variantId?: number; // Haravan variant ID for add to cart
}


export const CompletionPage: React.FC<CompletionPageProps> = ({
  designInfo,
  onBackToShop,
  shopUrl = 'https://custom.airxcoffee.com/cart',
  variantId
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [previousDesigns, setPreviousDesigns] = useState<PreviousDesign[]>([]);
  const [designThumbnails, setDesignThumbnails] = useState<Record<string, string>>({});

  // Helper function to get product type label
  const getProductTypeLabel = (productType: string): string => {
    const labels: Record<string, string> = {
      'phone_case': 'Ốp lưng điện thoại',
      'coffee_cup': 'Cốc bã cà phê'
    };
    return labels[productType] || productType;
  };

  // Helper to copy order ID
  const handleCopy = () => {
    navigator.clipboard.writeText(designInfo.orderId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Helper to handle back to shop (add to cart)
  const handleShopClick = () => {
    if (variantId) {
      try {
        addToHaravanCart({
          variantId: variantId,
          designId: designInfo.orderId,
          quantity: 1
        });
      } catch (err) {
        console.error('Failed to add to cart:', err);
        window.location.href = shopUrl;
      }
    } else if (onBackToShop) {
      onBackToShop();
    } else {
      window.location.href = shopUrl;
    }
  };

  // Format date helper
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  };

  // Load and save design history
  useEffect(() => {
    const manageHistory = async () => {
      try {
        const visitorId = localStorage.getItem('veritas_visitor_id');
        if (!visitorId) return;

        const storageKey = `designs_${visitorId}`;
        const savedDesignsStr = localStorage.getItem(storageKey);
        let savedDesigns: PreviousDesign[] = savedDesignsStr ? JSON.parse(savedDesignsStr) : [];
        
        // Ensure dates are Date objects
        savedDesigns = savedDesigns.map(d => ({...d, createdAt: new Date(d.createdAt)}));

        // Check if current design is already in history (by orderId)
        const exists = savedDesigns.some(d => d.orderId === designInfo.orderId);
        
        if (!exists) {
            // Add current design to history
            const newDesign: PreviousDesign = {
                id: designInfo.orderId,
                productType: designInfo.productType,
                createdAt: new Date(),
                orderId: designInfo.orderId,
                variantId: variantId,
                productName: designInfo.productName
            };
            
            // Add to front
            savedDesigns.unshift(newDesign);
            
            // Save back to localStorage
            localStorage.setItem(storageKey, JSON.stringify(savedDesigns));
            
            // Also save thumbnail to IndexedDB
            if (designInfo.mockupImageUrl) {
                 await indexedDBService.saveThumbnail(designInfo.orderId, designInfo.mockupImageUrl);
            }
        }
        
        // Sort by newest first
        savedDesigns.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setPreviousDesigns(savedDesigns);

        // Load thumbnails from IndexedDB
        const thumbnails: Record<string, string> = {};
        for (const d of savedDesigns) {
             const thumb = await indexedDBService.getThumbnail(d.orderId);
             if (thumb) thumbnails[d.orderId] = thumb;
        }
        setDesignThumbnails(thumbnails);

      } catch (err) {
        console.error('Failed to manage history:', err);
      }
    };

    manageHistory();
  }, [designInfo, variantId]);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col overflow-auto pb-24 md:pb-0">
      {/* Redesigned Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 py-1 flex items-center justify-between min-h-[64px]">
          {/* Left: Back & Logo */}
          <div className="flex items-center gap-1 sm:gap-4 flex-shrink-0">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Quay lại"
            >
              <ChevronLeft className="w-6 h-6 text-gray-700" />
            </button>
            <a 
              href="/" 
              className="flex items-center h-14 sm:h-20 overflow-visible py-1 cursor-pointer transition-transform hover:scale-105 active:scale-95"
            >
              <img 
                src={logo} 
                alt="AirX" 
                className="h-20 sm:h-32 w-auto object-contain transform -translate-y-1 drop-shadow-md" 
              />
            </a>
          </div>

          {/* Center: Title */}
          <h1 className="text-xs sm:text-base lg:text-lg font-extrabold text-gray-900 mx-1 text-center flex-1 line-clamp-1 uppercase tracking-tight">
            Hoàn Tất Thiết Kế
          </h1>

          {/* Right: My Design Button */}
          <div className="flex-shrink-0">
            <a
              href="/history"
              className="flex items-center gap-1.5 px-2.5 py-2 bg-black text-white hover:bg-gray-800 rounded-xl transition-all text-[10px] sm:text-xs font-bold shadow-md active:scale-95"
            >
              <Clock className="w-3.5 h-3.5" />
              <span>My design</span>
            </a>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-6xl mx-auto px-4 py-8 md:py-12">

        {/* Success Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
            <Sparkles className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            Hoàn thành thiết kế!
          </h1>
          <p className="text-slate-600 text-lg">
            Thiết kế của bạn đã được lưu thành công
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">

          {/* Left: Design Preview */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-2xl shadow-lg p-6"
          >
            <h2 className="text-xl font-bold text-slate-900 mb-4">Chiếc ly của bạn đã hoàn thành.</h2>
            <div className="relative bg-slate-50 rounded-xl overflow-hidden aspect-square flex items-center justify-center">
              <img
                src={designInfo.mockupImageUrl}
                alt="Mockup"
                className="w-full h-full object-contain"
              />
            </div>

            {/* Design Details */}
            <div className="mt-4 space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-slate-100">
                <span className="text-slate-600">Loại sản phẩm:</span>
                <span className="font-medium text-slate-900">
                  {getProductTypeLabel(designInfo.productType)}
                </span>
              </div>
              {designInfo.productName && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Mẫu:</span>
                  <span className="font-medium text-slate-900">{designInfo.productName}</span>
                </div>
              )}
              {designInfo.designOptions?.removeBackground !== undefined && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Tách nền:</span>
                  <span className="font-medium text-slate-900">
                    {designInfo.designOptions.removeBackground ? 'Có' : 'Không'}
                  </span>
                </div>
              )}
              {designInfo.designOptions?.textOverlay && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Chữ in:</span>
                  <span className="font-medium text-slate-900">
                    {designInfo.designOptions.textOverlay}
                  </span>
                </div>
              )}
              {designInfo.designOptions?.printType && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Kiểu in:</span>
                  <span className="font-medium text-slate-900">
                    {designInfo.designOptions.printType === 'laser' ? 'Khắc laser' : 'In lụa'}
                  </span>
                </div>
              )}
              {designInfo.designOptions?.scale !== undefined && (
                <div className="flex justify-between py-2 border-b border-slate-100">
                  <span className="text-slate-600">Kích thước:</span>
                  <span className="font-medium text-slate-900">
                    {designInfo.designOptions.scale}%
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Right: Order ID & Actions */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >

            {/* Order ID Card */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Mã đơn hàng của bạn</h2>

              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 mb-4 border border-indigo-100">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex-1">
                    <p className="text-xs text-slate-600 mb-1">Mã đơn hàng:</p>
                    <p className="font-mono text-lg font-bold text-slate-900 break-all">
                      {designInfo.orderId}
                    </p>
                  </div>
                  <button
                    onClick={handleCopy}
                    className="flex-shrink-0 p-3 bg-white rounded-lg hover:bg-indigo-50 transition-all shadow-sm border border-indigo-100 group"
                    title="Copy mã"
                  >
                    {copied ? (
                      <Check className="w-5 h-5 text-green-600" />
                    ) : (
                      <Copy className="w-5 h-5 text-slate-600 group-hover:text-indigo-600" />
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-amber-900 leading-relaxed">
                  <strong>Bấm "Thêm vào giỏ"</strong> và tiến hành thanh toán. Ly sẽ được giao đến trong vòng 0-2 ngày.
                </p>
              </div>

              {copied && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-3 text-center text-sm text-green-600 font-medium"
                >
                  ✓ Đã copy vào clipboard!
                </motion.div>
              )}
            </div>

            {/* Add to Cart Button - Desktop only */}
            <button
              onClick={handleShopClick}
              className="hidden md:flex w-full bg-[#06b6d4] text-white font-bold py-4 px-6 rounded-xl hover:bg-[#0891b2] transition-all shadow-lg hover:shadow-xl active:scale-98 items-center justify-center gap-3 text-base"
            >
              <ShoppingCart className="w-5 h-5" />
              Thêm vào giỏ
            </button>

            {/* Info Box */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
              <p className="text-xs text-slate-600 leading-relaxed">
                💡 <strong>Lưu ý:</strong> Thiết kế của bạn đã được lưu và có thể xem lại bất cứ lúc nào.
                Nhấn vào nút "Thêm vào giỏ" để hoàn tất đơn hàng của bạn.
              </p>
            </div>

          </motion.div>
        </div>

        {/* Previous Designs Section */}
        {previousDesigns.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-2xl font-bold text-slate-900 mb-6">
              Thiết kế trước đây của bạn
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {previousDesigns.map((design, index) => (
                <motion.div
                  key={design.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow overflow-hidden group"
                >
                  <div className="relative bg-slate-50 aspect-square overflow-hidden flex items-center justify-center">
                    {designThumbnails[design.orderId] ? (
                      <img
                        src={designThumbnails[design.orderId]}
                        alt={`Design ${design.orderId}`}
                        className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <div className="text-slate-400 text-6xl">
                        {design.productType === 'phone_case' ? '📱' : '☕'}
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-medium text-slate-900 mb-1">
                      {getProductTypeLabel(design.productType)}
                    </p>
                    <p className="text-xs text-slate-500 mb-2">
                      {formatDate(design.createdAt)}
                    </p>
                    <p className="text-xs font-mono text-slate-600 truncate" title={design.orderId}>
                      {design.orderId}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

      </div>

      {/* Sticky Add to Cart Button - Mobile only */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
      >
        <div className="px-4 py-3 safe-area-inset-bottom">
          <button
            onClick={handleShopClick}
            className="w-full bg-[#06b6d4] text-white font-bold py-4 px-6 rounded-xl hover:bg-[#0891b2] transition-all shadow-lg active:scale-98 flex items-center justify-center gap-3 text-base"
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Thêm vào giỏ</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default CompletionPage;
